import { useEffect, useState } from 'react';
import { List } from '@refinedev/antd';
import { Table, Switch, Button, Space, App as AntApp, Card, Skeleton, Tag, Row, Col, Statistic, Tooltip, InputNumber } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, CarOutlined, SwapOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

interface TariffConfig {
  id: number;
  tariff: 'ECONOM' | 'STANDARD' | 'COMFORT' | 'BUSINESS' | 'SEVEN_PLUS';
  isActive: boolean;
  sortOrder: number;
  carCount: number;
  tripCount: number;
  /// Passenger-seat counts admin allows for this tariff. Mobile filters
  /// the seat-count chips against this array; empty/missing → mobile
  /// falls back to the full hardcoded range so old API responses still
  /// render.
  allowedSeats?: number[];
}

// Per-tariff "full salon" passenger count — the value the mobile uses to
// represent the "To'liq salon" chip (book the whole vehicle). Mirrors
// ViloyatlarAroTariffExt.maxSeats on mobile and TARIFF_CONFIG.maxPassengers
// in viloyatlar-aro-v2.service.ts: Ekonom keeps the full 4-seat sedan back
// row + front passenger; Standard/Komfort/Biznes drop to 3 (front passenger
// seat isn't sold — comfort tier convention).
const FULL_SALON_VALUE: Record<TariffConfig['tariff'], number> = {
  ECONOM: 4,
  STANDARD: 3,
  COMFORT: 3,
  BUSINESS: 3,
  SEVEN_PLUS: 9,
};

// Individual numeric chips are 1..(full-1), capped at 3, then the
// "To'liq salon" chip at `full` — mirrors seat_count_selector.dart's
// `individualCap = min(cap-1, 3)`. Ekonom (full=4) renders 4 chips
// (1,2,3,To'liq salon); Standard/Komfort/Biznes (full=3) render 3
// (1,2,To'liq salon) — NOT a fixed 4-chip template, since a stale 4th
// "3 ta joy" chip would be a real, bookable-looking but invalid option.
const seatOptionsFor = (tariff: TariffConfig['tariff']): number[] => {
  const full = FULL_SALON_VALUE[tariff];
  const individualCap = Math.min(full - 1, 3);
  return [
    ...Array.from({ length: individualCap }, (_, i) => i + 1),
    full,
  ];
};

// Display label for a seat value within a given tariff. Anything that
// matches the tariff's full-salon value renders as "To'liq salon"; the
// individual passenger counts render as "1 ta joy" / "2 ta joy" / "3 ta joy".
// `t()` is parameterless so we compose the count + the (localized) unit
// suffix inline.
const seatLabel = (value: number, tariff: TariffConfig['tariff']): string => {
  if (value === FULL_SALON_VALUE[tariff]) return t('tariffs.fullSalon');
  return `${value} ${t('tariffs.seatUnit')}`;
};

const TARIFF_LABELS: Record<string, string> = {
  ECONOM: 'Ekonom',
  STANDARD: 'Standart',
  COMFORT: 'Komfort',
  BUSINESS: 'Biznes',
  SEVEN_PLUS: '7+',
};

const TARIFF_COLORS: Record<string, string> = {
  ECONOM: 'default',
  STANDARD: 'blue',
  COMFORT: 'cyan',
  BUSINESS: 'purple',
  SEVEN_PLUS: 'gold',
};

const CARD_ACCENT: Record<string, string> = {
  ECONOM: '#7F8C8D',
  STANDARD: '#1677FF',
  COMFORT: '#13C2C2',
  BUSINESS: '#722ED1',
  SEVEN_PLUS: '#D4AD00',
};

export const TariffList = () => {
  const { message } = AntApp.useApp();
  const [rows, setRows] = useState<TariffConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<TariffConfig[]>('/admin/tariffs');
      setRows(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('tariffs.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (row: TariffConfig, isActive: boolean) => {
    setBusyId(row.id);
    try {
      await apiClient.put(`/admin/tariffs/${row.id}`, { isActive });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive } : r)));
      message.success(t('tariffs.saved'));
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('tariffs.saveFailed'));
    } finally {
      setBusyId(null);
    }
  };

  // Toggle a single passenger-count value on/off for a tariff. Sends the
  // full resulting array — backend dedupes, sorts and validates.
  const toggleSeat = async (row: TariffConfig, seat: number) => {
    const current = row.allowedSeats ?? seatOptionsFor(row.tariff);
    const set = new Set(current);
    if (set.has(seat)) set.delete(seat); else set.add(seat);
    const allowedSeats = Array.from(set).sort((a, b) => a - b);
    setBusyId(row.id);
    // Optimistic update so the chip flips instantly.
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, allowedSeats } : r)));
    try {
      await apiClient.put(`/admin/tariffs/${row.id}`, { allowedSeats });
      message.success(t('tariffs.saved'));
    } catch (err: any) {
      // Revert on error.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, allowedSeats: current } : r)));
      message.error(err?.response?.data?.message || t('tariffs.saveFailed'));
    } finally {
      setBusyId(null);
    }
  };

  // SEVEN_PLUS uses the inline gender counter (not chips) on mobile —
  // admin configures the min + max passenger cap and we store it as a
  // contiguous range [min..max] in allowedSeats. Mobile's
  // InlinePassengerSelector reads min(allowedSeats) and max(allowedSeats)
  // to clamp the +/- buttons.
  const setSevenPlusRange = async (
    row: TariffConfig,
    min: number,
    max: number,
  ) => {
    const clampedMin = Math.max(1, Math.min(20, Math.round(min)));
    const clampedMax = Math.max(clampedMin, Math.min(20, Math.round(max)));
    const allowedSeats = Array.from(
      { length: clampedMax - clampedMin + 1 },
      (_, i) => i + clampedMin,
    );
    const previous = row.allowedSeats ?? seatOptionsFor(row.tariff);
    setBusyId(row.id);
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, allowedSeats } : r)),
    );
    try {
      await apiClient.put(`/admin/tariffs/${row.id}`, { allowedSeats });
      message.success(t('tariffs.saved'));
    } catch (err: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, allowedSeats: previous } : r,
        ),
      );
      message.error(err?.response?.data?.message || t('tariffs.saveFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const reordered = [...rows];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setRows(reordered);
    try {
      const res = await apiClient.put<TariffConfig[]>('/admin/tariffs/reorder', {
        orderedIds: reordered.map((r) => r.id),
      });
      setRows(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('tariffs.saveFailed'));
      load();
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: unknown, __: TariffConfig, idx: number) => idx + 1,
    },
    {
      title: t('tariffs.name'),
      dataIndex: 'tariff',
      key: 'tariff',
      render: (tariff: TariffConfig['tariff']) => (
        <Space>
          <Tag color={TARIFF_COLORS[tariff]}>{TARIFF_LABELS[tariff] || tariff}</Tag>
          <span style={{ color: '#7F8C8D', fontSize: 12 }}>{tariff}</span>
        </Space>
      ),
    },
    {
      title: t('tariffs.cars'),
      dataIndex: 'carCount',
      key: 'carCount',
      width: 100,
      render: (n: number) => <span>{n}</span>,
    },
    {
      title: t('tariffs.trips'),
      dataIndex: 'tripCount',
      key: 'tripCount',
      width: 100,
      render: (n: number) => <span>{n}</span>,
    },
    {
      title: t('tariffs.available'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, row: TariffConfig) => (
        <Switch
          checked={isActive}
          loading={busyId === row.id}
          onChange={(v) => toggleActive(row, v)}
        />
      ),
    },
    {
      title: (
        <Tooltip title={t('tariffs.allowedSeatsHint')}>
          <span>{t('tariffs.allowedSeats')}</span>
        </Tooltip>
      ),
      key: 'allowedSeats',
      render: (_: unknown, row: TariffConfig) => {
        // SEVEN_PLUS uses the inline gender counter on mobile — admin
        // sets both min and max passenger caps (1..20). The stored array
        // is the contiguous range [min..max], so mobile reads
        // min(allowedSeats) for the floor and max(allowedSeats) for the
        // ceiling.
        if (row.tariff === 'SEVEN_PLUS') {
          const seats = row.allowedSeats?.length
            ? row.allowedSeats
            : [1, 2, 3, 4, 5, 6, 7, 8, FULL_SALON_VALUE.SEVEN_PLUS];
          const currentMin = Math.min(...seats);
          const currentMax = Math.max(...seats);
          return (
            <Space>
              <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                {t('tariffs.minPassengers')}:
              </span>
              <InputNumber
                min={1}
                max={currentMax}
                value={currentMin}
                disabled={busyId === row.id}
                onChange={(v) => {
                  if (typeof v === 'number' && v !== currentMin) {
                    setSevenPlusRange(row, v, currentMax);
                  }
                }}
                style={{ width: 72 }}
              />
              <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                {t('tariffs.maxPassengers')}:
              </span>
              <InputNumber
                min={currentMin}
                max={20}
                value={currentMax}
                disabled={busyId === row.id}
                onChange={(v) => {
                  if (typeof v === 'number' && v !== currentMax) {
                    setSevenPlusRange(row, currentMin, v);
                  }
                }}
                style={{ width: 72 }}
              />
            </Space>
          );
        }

        const options = seatOptionsFor(row.tariff);
        const allowed = new Set(row.allowedSeats ?? options);
        return (
          <Space wrap size={4}>
            {options.map((seat) => {
              const enabled = allowed.has(seat);
              return (
                <Button
                  key={seat}
                  size="small"
                  type={enabled ? 'primary' : 'default'}
                  disabled={busyId === row.id}
                  onClick={() => toggleSeat(row, seat)}
                  style={{
                    padding: '0 12px',
                    opacity: enabled ? 1 : 0.55,
                  }}
                >
                  {seatLabel(seat, row.tariff)}
                </Button>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: t('tariffs.order'),
      key: 'order',
      width: 140,
      render: (_: unknown, _row: TariffConfig, idx: number) => (
        <Space>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={idx === 0}
            onClick={() => move(idx, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={idx === rows.length - 1}
            onClick={() => move(idx, 1)}
          />
        </Space>
      ),
    },
  ];

  return (
    <List title={t('tariffs.title')}>
      {/* Stat cards — one per tariff, shows car and trip counts */}
      {rows.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {rows.map((row) => {
            const accent = CARD_ACCENT[row.tariff] || '#1677FF';
            return (
              <Col key={row.id} xs={24} sm={12} md={8} lg={Math.max(4, Math.floor(24 / rows.length))}>
                <Card
                  bordered
                  style={{
                    borderTop: `3px solid ${accent}`,
                    opacity: row.isActive ? 1 : 0.55,
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space>
                      <Tag color={TARIFF_COLORS[row.tariff]} style={{ margin: 0 }}>
                        {TARIFF_LABELS[row.tariff] || row.tariff}
                      </Tag>
                      {!row.isActive && (
                        <Tag color="red" style={{ margin: 0 }}>
                          {t('tariffs.inactive')}
                        </Tag>
                      )}
                    </Space>
                    <Row gutter={8} style={{ marginTop: 8 }}>
                      <Col span={12}>
                        <Statistic
                          title={
                            <Space size={4}>
                              <CarOutlined />
                              {t('tariffs.cars')}
                            </Space>
                          }
                          value={row.carCount}
                          valueStyle={{ fontSize: 22, fontWeight: 600, color: accent }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={
                            <Space size={4}>
                              <SwapOutlined />
                              {t('tariffs.trips')}
                            </Space>
                          }
                          value={row.tripCount}
                          valueStyle={{ fontSize: 22, fontWeight: 600, color: accent }}
                        />
                      </Col>
                    </Row>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={
          loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 4 }} /></Card> }
            : false
        }
      />
    </List>
  );
};
